import type { Dayjs } from "@thalamos/common";
import type {
  PatientStateWithContext,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import {
  formIsInProgress,
  formIsRecordOfDetention,
  getActiveSection136,
  isInDualStatus,
} from "@thalamos/aspire-common";

/**
 * Whether the restored (merged-away) patient keeps its own MHA status after an
 * unmerge, rather than being reset to `Incomplete`.
 *
 * True when the still-merged pair is in dual status, or when the restored
 * patient has its own active Section 136 — an unmerge preserves a dual status
 * and never overwrites an active 136, so that status survives; any other status
 * (a non-136 detention or an intermediary) is replaced by the reset.
 *
 * The 136 check uses `restoredPatientOwnStates`, not `survivorCurrentStates`:
 * a merged view surfaces only one 136 when both patients hold one, hiding the
 * restored patient's.
 */
export function restoredPatientKeepsOwnStatusOnUnmerge(
  survivorCurrentStates: PatientStateWithContext[],
  restoredPatientOwnStates: PatientStateWithContext[],
  asOf: Dayjs,
): boolean {
  return (
    isInDualStatus(survivorCurrentStates, asOf) ||
    getActiveSection136(restoredPatientOwnStates, asOf) !== undefined
  );
}

export function hasInProgressWork(
  patientTimeline: PatientTimelineResponse | undefined,
) {
  const patientTimelineAdmissions = patientTimeline?.inProgressWork?.admissions;

  const hasStandaloneOrMultiFormsInProgress =
    patientTimeline?.inProgressWork?.items?.find((i) =>
      i.forms.find((i) => formIsInProgress(i)),
    );

  // This is checking that every admission within the form context has a completed h3 form, if it does we should treat the admission not in progress anymore
  const allAdmissionsComplete = (
    patientTimeline?.inProgressWork?.admissions || []
  ).every((admission) =>
    admission.forms.some(
      (form) => formIsRecordOfDetention(form) && form.status === "complete",
    ),
  );

  const hasFormInProgressForms =
    hasStandaloneOrMultiFormsInProgress ||
    (patientTimelineAdmissions &&
      patientTimelineAdmissions.length > 0 &&
      !allAdmissionsComplete);

  return hasFormInProgressForms;
}
